import { useState } from 'react';
import { IRaffleLine } from 'types/Draws';
import { ASSET_PREFIX } from 'utils/constants';

interface INewRaffleTicketProps {
  rafflePrizeGroup?: string;
  lines: IRaffleLine[];
  raffleProofLink?: JSX.Element;
  position?: number | null;
}

const NewRaffleTicket = ({
  rafflePrizeGroup,
  lines,
  raffleProofLink,
  position,
}: INewRaffleTicketProps) => {
  const numLines = lines.length;
  const [expanded, setExpanded] = useState(false);

  let backgroundUrl;
  switch (rafflePrizeGroup) {
    case 'DAILY_POUND':
      backgroundUrl = `${ASSET_PREFIX}/images/daily-pound-raffle/dpr_ticket.svg`;
      break;

    case 'UKRAINE_RELIEF':
      backgroundUrl = position
        ? `${ASSET_PREFIX}/images/ukraine-relief-raffle/urr-ticket-no-right-side.svg`
        : `${ASSET_PREFIX}/images/ukraine-relief-raffle/urr-ticket-no-shadow.svg`;
      break;

    case 'ICELAND':
      backgroundUrl = `${ASSET_PREFIX}/images/iceland-raffle/iceland-raffle-ticket.svg`;
      break;
    case undefined:
    case null:
      break;

    default:
      backgroundUrl = `${ASSET_PREFIX}/images/common/default-raffle-ticket.svg`;
  }

  const handleExpandTickets = () => {
    setExpanded(!expanded);
  };

  return (
    <div
      className="newRaffleTicket flexCentered"
      onClick={handleExpandTickets}
      style={{ cursor: `${numLines > 1 ? 'pointer' : ''}` }}
    >
      {numLines === 1 &&
        (position ? (
          <div className="ticket">
            <div className="ticketWithPositionLabels flexSpaceBetween">
              <p
                className="positionLabel"
                style={{
                  visibility:
                    'hidden' /* leave it in, so it doesn't mess up spacing for the other side */,
                }}
              >
                {position}
              </p>
              <p>{lines[0].raffle}</p>
              <p className="positionLabel">{position}</p>
            </div>
          </div>
        ) : (
          <div className="ticket">
            <p>{lines[0].raffle}</p>
          </div>
        ))}

      {numLines > 1 && !expanded && (
        <div id="collapsed">
          <p style={{ margin: '1em 0 0.5em' }}>
            <i className="clickOrTap"> to view all</i>
          </p>
          <img
            src={`${ASSET_PREFIX}/images/common/chevron-down-icon.png`}
            alt=""
            style={{ maxWidth: '15px', marginBottom: '2em' }}
          />
          <>
            <div
              key="0"
              className="ticket"
              style={{ position: 'relative', zIndex: numLines }}
            >
              <p>{lines[0].raffle}</p>
            </div>
            {lines.slice(1).map((line, i) => (
              <div
                key={i}
                className="ticket"
                style={{
                  position: 'absolute',
                  zIndex: numLines - (i + 1),
                  transform: `translate(${7 * (i + 1)}px, -${7 * (i + 1)}px)`,
                  opacity: `${1 - 0.2 * (i + 1)}`,
                }}
              />
            ))}
          </>
        </div>
      )}

      {numLines > 1 && expanded && (
        <div id="expanded">
          <p style={{ margin: '1em 0 0.5em' }}>
            <i className="clickOrTap"> to hide</i>
          </p>
          <img
            src={`${ASSET_PREFIX}/images/iceland-raffle/chevron-up-icon.png`}
            alt=""
            style={{ maxWidth: '15px', marginBottom: '2em' }}
          />
          <>
            {lines.map((line, i) => (
              <div
                key={i}
                className={`ticket ${i > 0 ? 'notFirst' : ''}`}
                style={{ position: 'relative', zIndex: numLines - i }}
              >
                <div>
                  <p>{line.raffle}</p>
                </div>
              </div>
            ))}
          </>
        </div>
      )}

      {raffleProofLink}

      <style jsx>
        {`
          .newRaffleTicket {
            margin: 0.5em;
            isolation: isolate;
          }

          .clickOrTap:before {
            content: 'Tap';
          }

          #collapsed {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            align-items: center;
          }

          #expanded {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }

          .ticket {
            width: 264px;
            height: 88px;
            display: flex;
            justify-content: center;
            align-items: center;
            background: url(${backgroundUrl});
            background-size: cover;
          }

          .ticket p {
            font-size: 18px;
            font-weight: 700;
            letter-spacing: 5px;
            cursor: default;
          }

          #collapsed .notFirst {
            margin-top: -92px;
            margin-left: 10px;
            opacity: 0.8;
          }

          #expanded .notFirst {
            margin-top: -20px;
          }

          .ticketWithPositionLabels {
            width: 90%;
          }

          .ticketWithPositionLabels p {
            margin: 0;
            text-align: center;
          }

          .ticketWithPositionLabels p.positionLabel {
            letter-spacing: 0;
          }

          @media (min-width: 500px) {
            .clickOrTap:before {
              content: 'Click';
            }

            .ticket {
              width: 450px;
              height: 151px;
              border-radius: 9px;
            }

            .ticket p {
              font-size: 32px;
              letter-spacing: 8px;
            }

            .notFirst {
              margin-top: -30px;
            }
          }

          :global(.lineContainer) {
            align-self: center;
          }

          :global(.number) {
            font-size: 18px !important;
            letter-spacing: 0.1em !important;
          }
        `}
      </style>
    </div>
  );
};

export default NewRaffleTicket;
